import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Chip as MuiChip, ChipProps, styled } from '@mui/material';
import { getContrastRatio, type Theme } from '@mui/material/styles';
import { forwardRef } from 'react';

function contrastRatio(foreground: string, background: string) {
  try {
    if (!foreground || !background) {
      return 0;
    }
    return getContrastRatio(foreground, background);
  } catch {
    return 0;
  }
}

function readableText(background: string, theme: Theme) {
  const { palette } = theme;
  const preferred = palette.text.primary;
  if (contrastRatio(preferred, background) >= 4.5) {
    return preferred;
  }

  const onBlack = contrastRatio(palette.common.black, background);
  const onWhite = contrastRatio(palette.common.white, background);
  if (onBlack === 0 && onWhite === 0) {
    // Background is a CSS variable: no ratio can be computed, follow the colour scheme instead.
    return (theme.vars || theme).palette.text.primary;
  }

  return onBlack >= onWhite ? palette.common.black : palette.common.white;
}

function chipSurface(backgroundColor: string | undefined, theme: Theme) {
  if (backgroundColor) {
    return backgroundColor;
  }

  const palette = (theme.vars || theme).palette;
  return theme.palette.mode === 'dark' ? palette.grey[700] : palette.grey.A100;
}

interface Props
  extends Pick<
    ChipProps,
    | 'avatar'
    | 'label'
    | 'disabled'
    | 'size'
    | 'onClick'
    | 'sx'
    | 'onDelete'
    | 'deleteIcon'
    | 'clickable'
    | 'component'
  > {
  backgroundColor?: string;
  textColor?: string;
  rounded?: boolean;
}

const StyledChip = styled(MuiChip, {
  shouldForwardProp: (prop) =>
    prop !== 'backgroundColor' && prop !== 'textColor' && prop !== 'rounded',
})<Pick<Props, 'backgroundColor' | 'textColor' | 'rounded'>>(
  ({ theme, backgroundColor, textColor, rounded }) => {
    const background = chipSurface(backgroundColor, theme);
    const color =
      textColor ||
      (backgroundColor
        ? readableText(background, theme)
        : (theme.vars || theme).palette.text.primary);

    return {
      fontWeight: 500,
      background,
      color,
      '&.MuiChip-sizeSmall': {
        borderRadius: rounded ? Number(theme.shape.borderRadius) * 100 : theme.shape.borderRadius,
        [theme.breakpoints.down('sm')]: {
          height: 'auto',
        },
        '& .MuiChip-label': {
          ...theme.typography.body3,
          color,
          padding: theme.spacing(1, 1.5),
          [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(1.5, 2.25),
            ...theme.typography.body1,
          },
        },
      },
      '&.MuiChip-sizeMedium': {
        borderRadius: rounded
          ? Number(theme.shape.borderRadius) * 100
          : Number(theme.shape.borderRadius) * 2,
        [theme.breakpoints.down('sm')]: {
          height: 'auto',
        },
        '& .MuiChip-label': {
          ...theme.typography.body2,
          color,
          padding: theme.spacing(1.25, 2),
          [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(1.5, 2.25),
            ...theme.typography.body1,
          },
        },
      },
      '& .MuiChip-deleteIcon': {
        color,
        marginRight: theme.spacing(1.5),
        fontSize: '18px',
      },
    };
  }
);

const Chip = forwardRef<HTMLDivElement, Props>(
  ({ backgroundColor, textColor, rounded = false, ...rest }, ref) => {
    return (
      <StyledChip
        ref={ref}
        backgroundColor={backgroundColor}
        textColor={textColor}
        variant="filled"
        rounded={rounded}
        deleteIcon={<CloseRoundedIcon />}
        {...rest}
      />
    );
  }
);

export default Chip;
